/**
 * AR Viewer — place framed artwork on your wall using the phone camera.
 * V1: getUserMedia camera feed + canvas overlay.
 * User taps to place the artwork, pinch/drag to resize and reposition.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button, cn } from '@frameit/ui';
import { useSelectedArtwork, getLocalImageUrl } from '../../stores/projectStore';
import { getFrameColor } from '../../data/frames';
import { getMatColor } from '../../data/mats';
import { buildCssFilter } from '../editor/utils/color-filters';
import { renderFrame } from '../editor/renderers/frame-renderer';

interface ArtworkPosition {
  x: number; // percentage 0-100
  y: number;
  scale: number; // 0.1-2.0
}

export interface ARViewerProps {
  onClose: () => void;
  className?: string;
}

/** AR Viewer component */
export function ARViewer({ onClose, className }: ARViewerProps): React.JSX.Element {
  const selectedArtwork = useSelectedArtwork();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaced, setIsPlaced] = useState(false);
  const [position, setPosition] = useState<ArtworkPosition>({ x: 50, y: 40, scale: 0.4 });

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);

  // Pinch zoom state
  const lastPinchDistRef = useRef<number | null>(null);

  // Get artwork image URL
  const imageUrl = useMemo(() => {
    if (!selectedArtwork) return null;
    return getLocalImageUrl(selectedArtwork.artworkId) ?? null;
  }, [selectedArtwork]);

  // Frame rendering
  const frameColor = selectedArtwork ? getFrameColor(selectedArtwork.frame.templateId) : '#1a1a1a';
  const frameWidth = (selectedArtwork?.frame.width ?? 25) * 0.25;
  const matWidth = selectedArtwork?.mat ? (selectedArtwork.mat.width ?? 0.1) * 100 : 0;
  const totalBorder = frameWidth + matWidth;
  const matColor = selectedArtwork?.mat ? getMatColor(selectedArtwork.mat.templateId) : undefined;
  const colorFilter = selectedArtwork ? buildCssFilter(selectedArtwork.colorGrading) : 'none';

  // Frame canvas image
  const frameImage = useMemo(() => {
    if (!selectedArtwork) return undefined;
    try {
      const innerW = 200;
      const innerH = 200;
      const canvas = renderFrame({
        templateId: selectedArtwork.frame.templateId,
        frameWidth: Math.round(frameWidth),
        innerWidth: innerW + Math.round(matWidth * 2),
        innerHeight: innerH + Math.round(matWidth * 2),
      });
      return canvas.toDataURL();
    } catch {
      return undefined;
    }
  }, [selectedArtwork, frameWidth, matWidth]);

  // Shadow
  const shadow = selectedArtwork?.shadow;
  const shadowCSS = shadow?.enabled
    ? `${(shadow.offsetX ?? 4) * 0.5}px ${(shadow.offsetY ?? 4) * 0.5}px ${(shadow.blur ?? 12) * 0.5}px rgba(0,0,0,${shadow.opacity ?? 0.3})`
    : 'none';

  // Start camera
  useEffect(() => {
    let cancelled = false;
    let localStream: MediaStream | null = null;

    const startCamera = async (): Promise<void> => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment', // Back camera
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        localStream = stream;
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        if (!cancelled) {
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof DOMException && err.name === 'NotAllowedError'
              ? 'Camera permission denied. Please allow camera access and try again.'
              : "Failed to access camera. Make sure you're using HTTPS.",
          );
          setIsLoading(false);
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      // Stop the local stream reference (covers race condition where
      // streamRef.current may not be set yet when cleanup runs)
      if (localStream) {
        localStream.getTracks().forEach((t) => t.stop());
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Handle tap to place
  const handleTap = useCallback(
    (clientX: number, clientY: number) => {
      if (!containerRef.current || isDragging) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 100;
      const y = ((clientY - rect.top) / rect.height) * 100;
      setPosition((prev) => ({ ...prev, x, y }));
      setIsPlaced(true);
    },
    [isDragging],
  );

  // Drag handlers
  const handleDragStart = useCallback(
    (clientX: number, clientY: number) => {
      if (!isPlaced) return;
      setIsDragging(true);
      dragStartRef.current = { x: clientX, y: clientY, posX: position.x, posY: position.y };
    },
    [isPlaced, position.x, position.y],
  );

  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging || !dragStartRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((clientX - dragStartRef.current.x) / rect.width) * 100;
      const dy = ((clientY - dragStartRef.current.y) / rect.height) * 100;
      setPosition((prev) => ({
        ...prev,
        x: Math.max(10, Math.min(90, dragStartRef.current!.posX + dx)),
        y: Math.max(10, Math.min(90, dragStartRef.current!.posY + dy)),
      }));
    },
    [isDragging],
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  // Mouse events for drag
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => handleDragMove(e.clientX, e.clientY);
    const onUp = () => handleDragEnd();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Touch events for pinch zoom + drag
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        // Pinch start
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastPinchDistRef.current = Math.hypot(dx, dy);
      } else if (e.touches.length === 1 && isPlaced) {
        handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    [isPlaced, handleDragStart],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && lastPinchDistRef.current !== null) {
        // Pinch zoom
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        const delta = dist / lastPinchDistRef.current;
        lastPinchDistRef.current = dist;
        setPosition((prev) => ({
          ...prev,
          scale: Math.max(0.1, Math.min(2, prev.scale * delta)),
        }));
      } else if (e.touches.length === 1) {
        handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    },
    [handleDragMove],
  );

  const handleTouchEnd = useCallback(() => {
    lastPinchDistRef.current = null;
    handleDragEnd();
  }, [handleDragEnd]);

  // Size controls
  const increaseSize = (): void => {
    setPosition((prev) => ({ ...prev, scale: Math.min(2, prev.scale + 0.05) }));
  };
  const decreaseSize = (): void => {
    setPosition((prev) => ({ ...prev, scale: Math.max(0.1, prev.scale - 0.05) }));
  };

  return (
    <div className={cn('fixed inset-0 z-[70] bg-black flex flex-col', className)}>
      {/* Camera feed */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden"
        onClick={(e) => {
          if (!isPlaced) handleTap(e.clientX, e.clientY);
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          playsInline
          muted
          autoPlay
        />

        {/* Loading state */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center text-white">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm">Starting camera...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center text-white max-w-xs">
              <svg
                className="w-12 h-12 mx-auto mb-3 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.924-.833-2.693 0L4.07 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <p className="text-sm mb-3">{error}</p>
              <Button variant="outline" size="sm" onClick={onClose}>
                Go Back
              </Button>
            </div>
          </div>
        )}

        {/* Tap hint */}
        {!isPlaced && !isLoading && !error && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-white">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full border-2 border-white/60 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white/60"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11"
                  />
                </svg>
              </div>
              <p className="text-sm font-medium">Tap your wall to place artwork</p>
              <p className="text-xs text-white/60 mt-1">Point camera at the wall first</p>
            </div>
          </div>
        )}

        {/* Placed artwork overlay */}
        {isPlaced && imageUrl && selectedArtwork && (
          <div
            className={cn(
              'absolute pointer-events-auto select-none',
              isDragging ? 'cursor-grabbing' : 'cursor-grab',
            )}
            style={{
              left: `${position.x}%`,
              top: `${position.y}%`,
              transform: `translate(-50%, -50%) scale(${position.scale})`,
              transformOrigin: 'center center',
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDragStart(e.clientX, e.clientY);
            }}
          >
            {/* Frame + Mat + Artwork */}
            <div
              className="relative inline-block"
              style={{
                padding: `${totalBorder}px`,
                backgroundColor: frameColor,
                backgroundImage: frameImage ? `url(${frameImage})` : undefined,
                backgroundSize: '100% 100%',
                boxShadow: shadowCSS,
              }}
            >
              {/* Mat */}
              {selectedArtwork.mat && matColor && (
                <div
                  className="absolute"
                  style={{
                    inset: `${frameWidth}px`,
                    backgroundColor: matColor,
                    boxShadow:
                      'inset 1px 1px 3px rgba(255,255,255,0.15), inset -1px -1px 2px rgba(0,0,0,0.1)',
                  }}
                />
              )}

              {/* Artwork image */}
              <img
                src={imageUrl}
                alt="Artwork"
                className="block"
                draggable={false}
                style={{
                  width: 200,
                  height: 200,
                  objectFit: 'contain',
                  filter: colorFilter !== 'none' ? colorFilter : undefined,
                }}
              />
            </div>
          </div>
        )}

        {/* Wall guide overlay */}
        {!isPlaced && !isLoading && !error && (
          <div className="absolute inset-0 pointer-events-none">
            {/* Grid lines for alignment */}
            <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/20" />
            <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/20" />
            <div className="absolute top-1/3 left-0 right-0 h-px bg-white/20" />
            <div className="absolute top-2/3 left-0 right-0 h-px bg-white/20" />
          </div>
        )}
      </div>

      {/* Controls bar */}
      <div className="bg-black/90 px-4 py-3 flex items-center justify-between safe-area-bottom">
        <Button variant="ghost" size="sm" className="text-white" onClick={onClose}>
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          Close
        </Button>

        {/* Size controls */}
        {isPlaced && (
          <div className="flex items-center gap-3">
            <button
              onClick={decreaseSize}
              className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <span className="text-white text-xs w-12 text-center">
              {Math.round(position.scale * 100)}%
            </span>
            <button
              onClick={increaseSize}
              className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Reset placement */}
        {isPlaced && (
          <Button
            variant="ghost"
            size="sm"
            className="text-white"
            onClick={() => setIsPlaced(false)}
          >
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}
